import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useInvestigation } from '../../../contexts/InvestigationContext';
import { format, differenceInDays } from 'date-fns';
import { nb } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import { uploadFile } from '../../../services/storageService';

const { FiUserCheck, FiFileText, FiMessageCircle, FiLogOut, FiAlertCircle, FiInfo, FiSend, FiShield, FiCheckCircle, FiHelpCircle, FiBookOpen, FiX, FiChevronRight, FiCalendar, FiClock, FiLoader, FiUpload, FiCheck, FiCornerUpLeft, FiPaperclip } = FiIcons;

const EvidenceUploadModal = ({ request, onCancel, onSuccess }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);

    try {
      const urls = [];
      for (const file of files) {
        const { url, error } = await uploadFile(file, 'evidence', `evidence_req_${request.id}_${Date.now()}_${file.name}`);
        if (error) throw error;
        urls.push(url);
      }

      const { error } = await supabase
        .from('evidence_requests')
        .update({ 
          status: 'fulfilled', 
          evidence_urls: urls,
          fulfilled_at: new Date().toISOString()
        })
        .eq('id', request.id);

      if (error) throw error;
      onSuccess();
    } catch (err) {
      console.error("Upload error:", err);
      alert("Kunne ikke laste opp filer.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white dark:bg-gray-800 w-full max-w-md rounded-3xl shadow-2xl p-8 border dark:border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-black uppercase tracking-widest dark:text-white">Last opp bevis</h2>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><SafeIcon icon={FiX} /></button>
        </div>
        
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800">
          <p className="text-xs font-black text-blue-900 dark:text-blue-100 uppercase tracking-widest mb-1">{request.title}</p>
          <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">{request.description}</p>
        </div>

        <div className="space-y-4">
          <label className="block w-full cursor-pointer group">
            <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl p-8 text-center hover:border-blue-500 transition-all">
              <SafeIcon icon={FiUpload} className="text-3xl text-gray-400 group-hover:text-blue-500 mx-auto mb-3" />
              <p className="text-sm font-bold text-gray-600 dark:text-gray-400">Klikk for å velge filer</p>
              <input type="file" multiple className="hidden" onChange={e => setFiles([...e.target.files])} />
            </div>
          </label>

          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-xl">
                  <span className="text-xs font-bold truncate max-w-[200px]">{f.name}</span>
                  <button onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-red-500 hover:text-red-700"><SafeIcon icon={FiX} /></button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-900/20 hover:bg-blue-700 transition-all disabled:opacity-50"
          >
            {uploading ? 'Laster opp...' : 'Fullfør innsending'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const AppealModal = ({ reportId, onCancel, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSubmitting(true);

    try {
      let evidenceUrls = [];
      if (files.length > 0) {
        for (const file of files) {
          const { url } = await uploadFile(file, 'evidence', `appeal_${reportId}_${Date.now()}_${file.name}`);
          if (url) evidenceUrls.push(url);
        }
      }

      const { error } = await supabase
        .from('case_appeals_1788622000000')
        .insert({
          report_id: reportId,
          reason: reason,
          evidence_urls: evidenceUrls,
          status: 'pending'
        });

      if (error) throw error;
      onSuccess();
    } catch (err) {
      console.error("Appeal error:", err);
      alert("Kunne ikke sende inn anke.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white dark:bg-gray-800 w-full max-w-xl rounded-[32px] shadow-2xl p-10 border dark:border-gray-700">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Anke på konklusjon</h2>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Sak: {reportId}</p>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><SafeIcon icon={FiX} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-amber-50 border border-amber-100 p-4 rounded-2xl">
            <p className="text-xs text-amber-800 leading-relaxed font-medium">
              Du har rett til å anke konklusjonen dersom du mener det foreligger feil i faktum, saksbehandlingsfeil eller at konklusjonen er urimelig basert på bevisene.
            </p>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Begrunnelse for anke *</label>
            <textarea
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={8}
              className="w-full px-6 py-5 rounded-[24px] bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-blue-500 text-gray-900 dark:text-white font-medium outline-none transition-all resize-none"
              placeholder="Beskriv hvorfor du mener konklusjonen bør vurderes på nytt..."
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Støttedokumentasjon (valgfritt)</label>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-900 rounded-xl cursor-pointer hover:bg-gray-200 transition-colors">
                <SafeIcon icon={FiPaperclip} className="text-blue-500" />
                <span className="text-[10px] font-black uppercase tracking-widest">Legg til vedlegg</span>
                <input type="file" multiple className="hidden" onChange={e => setFiles([...files, ...e.target.files])} />
              </label>
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-xl border border-blue-100 dark:border-blue-800">
                  <span className="text-[10px] font-bold truncate max-w-[100px]">{f.name}</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))}><SafeIcon icon={FiX} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-8 py-4 text-gray-500 font-black text-xs uppercase tracking-widest hover:text-gray-900 transition-colors"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={submitting || !reason.trim()}
              className="px-10 py-4 bg-blue-600 text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-900/20 disabled:opacity-50"
            >
              {submitting ? 'Sender...' : 'Send Anke'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const RightsModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()} >
        {/* Header */}
        <div className="bg-blue-600 p-6 text-white flex justify-between items-start">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
              <SafeIcon icon={FiShield} className="text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Dine rettigheter</h2>
              <p className="text-blue-100 text-sm">Informasjon til deg som er omvarslet</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors" >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-6">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800 leading-relaxed">
            Som omvarslet i en varslingssak har du sterke rettigheter i henhold til arbeidsmiljøloven og personvernforordningen (GDPR). Denne veilederen gir deg oversikt over prosessen.
          </div>
          <div className="space-y-4">
            <InfoSection title="1. Rett til kontradiksjon" text="Du har rett til å bli gjort kjent med beskyldningene som er rettet mot deg. Du skal få mulighet til å imøtegå påstandene og gi din versjon av saken før arbeidsgiver konkluderer. Dette er et grunnleggende rettssikkerhetsprinsipp." />
            <InfoSection title="2. Rett til innsyn" text="Du har rett til innsyn i personopplysninger som er lagret om deg i saken. Vær oppmerksom på at varslerens identitet kan være skjermet dersom dette er helt nødvendig for å beskytte varsleren mot gjengjeldelse, eller for å sikre en forsvarlig undersøkelse." />
            <InfoSection title="3. Rett til bistand" text="Du har rett til å la deg bistå av en tillitsvalgt, advokat eller en annen rådgiver (f.eks. verneombud) i alle møter med arbeidsgiver som omhandler saken." />
            <InfoSection title="4. Konfidensialitet" text="Saken skal behandles konfidensielt. Det betyr at kun de personene som strengt tatt trenger å vite om saken for å kunne behandle den, skal ha tilgang til opplysningene. Arbeidsgiver har taushetsplikt om sensitive personopplysninger." />
            <InfoSection title="5. Vern mot gjengjeldelse" text="Arbeidsgiver har et ansvar for å sikre at ingen involvere parter utsettes for gjengjeldelse eller usaklig behandling som følge av varslingssaken. Dette gjelder også deg som omvarslet." />
            <InfoSection title="6. Sletting av data" text="Personopplysninger skal slettes når de ikke lenger er nødvendige for formålet med behandlingen. Dersom varselet viser seg å være grunnløst, skal opplysningene normalt slettes umiddelbart." />
          </div>
        </div>
        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm" >
            Lukk veileder
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const InfoSection = ({ title, text }) => (
  <div className="border border-gray-200 rounded-xl p-4 hover:border-blue-200 hover:bg-blue-50/30 transition-colors group">
    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2 group-hover:text-blue-700">
      <SafeIcon icon={FiCheckCircle} className="text-blue-500 text-sm" /> {title}
    </h3>
    <p className="text-sm text-gray-600 leading-relaxed pl-6">
      {text}
    </p>
  </div>
);

const ReportedDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { submitCaseResponse, reports } = useInvestigation();
  const [responseContent, setResponseContent] = useState('');
  const [wantsDialogue, setWantsDialogue] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showRightsModal, setShowRightsModal] = useState(false);
  const [evidenceRequests, setEvidenceRequests] = useState([]);
  const [activeRequest, setActiveRequest] = useState(null);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [existingAppeal, setExistingAppeal] = useState(null);

  // Access restricted data from the session
  const sanitizedDescription = user?.accessCodeData?.sanitizedDescription || 'Beskrivelse ikke tilgjengelig.';
  const reportId = user?.reportId || 'Ukjent';
  
  // Find full report data if available in context
  const fullReport = reports?.find(r => r.id === reportId);
  const isResolved = fullReport?.status === 'resolved';
  
  // Deadline logic
  const deadlineDate = user?.accessCodeData?.deadline_reported_reply || null;
  const now = new Date();
  const isOverdue = deadlineDate && now > new Date(deadlineDate);
  const daysLeft = deadlineDate ? differenceInDays(new Date(deadlineDate), now) : null;

  useEffect(() => {
    const fetchRequests = async () => {
      const { data } = await supabase
        .from('evidence_requests')
        .select('*')
        .eq('report_id', reportId)
        .eq('target_role', 'reported_party')
        .eq('status', 'pending');
      if (data) setEvidenceRequests(data);
    };

    const fetchAppeal = async () => {
      const { data } = await supabase
        .from('case_appeals_1788622000000')
        .select('*')
        .eq('report_id', reportId)
        .maybeSingle();
      if (data) setExistingAppeal(data);
    };

    if (reportId !== 'Ukjent') {
      fetchRequests();
      fetchAppeal();
    }
  }, [reportId]);

  const handleLogout = () => {
    logout();
    navigate('/');
    // Fallback for å sikre redirect
    setTimeout(() => {
      if (window.location.pathname.includes('reported')) {
        window.location.href = "/";
      }
    }, 100);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!responseContent.trim()) return;
    setIsSubmitting(true);
    // Simulate API submission
    setTimeout(() => {
      submitCaseResponse(user?.accessCodeData?.id, reportId, responseContent, wantsDialogue);
      setIsSubmitted(true);
      setIsSubmitting(false);
    }, 1000);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white max-w-lg w-full rounded-xl shadow-lg p-8 text-center" >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="text-green-600 text-4xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Tilsvar sendt</h2>
          <p className="text-gray-600 mb-6"> Ditt tilsvar er registrert i saken. Saksbehandler vil gjennomgå informasjonen. Takk for at du bidrar til sakens opplysning. </p>
          <button onClick={handleLogout} className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors" >
            Logg ut
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AnimatePresence>
        {showRightsModal && <RightsModal onClose={() => setShowRightsModal(false)} />}
        {showAppealModal && (
          <AppealModal 
            reportId={reportId} 
            onCancel={() => setShowAppealModal(false)} 
            onSuccess={() => {
              setShowAppealModal(false);
              window.location.reload();
            }}
          />
        )}
        {activeRequest && (
          <EvidenceUploadModal 
            request={activeRequest} 
            onCancel={() => setActiveRequest(null)} 
            onSuccess={() => {
              setEvidenceRequests(prev => prev.filter(r => r.id !== activeRequest.id));
              setActiveRequest(null);
            }}
          />
        )}
      </AnimatePresence>
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiUserCheck} className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Innsyn i sak: {reportId}</h1>
                <p className="text-xs text-gray-500">Begrenset innsyn for omvarslet part</p>
              </div>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-gray-600 hover:text-red-600 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors" >
              <SafeIcon icon={FiLogOut} /> <span>Logg ut</span>
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Appeal Section (Only visible when resolved) */}
        {isResolved && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-indigo-900 text-white p-8 rounded-[32px] shadow-2xl relative overflow-hidden group"
          >
            <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-1000"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-md">
                  <SafeIcon icon={FiCheckCircle} className="text-3xl text-indigo-200" />
                </div>
                <div>
                  <h2 className="text-2xl font-black uppercase tracking-tight">Saken er konkludert</h2>
                  <p className="text-indigo-200 font-bold uppercase text-[10px] tracking-widest mt-1">Status: {fullReport.outcome_type || 'Fullført'}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="space-y-4">
                  <p className="text-indigo-100 text-sm leading-relaxed font-medium">
                    Arbeidsgiver har nå fattet en konklusjon i saken. Du kan se oppsummeringen av utfallet og iverksatte tiltak nedenfor. 
                  </p>
                  {existingAppeal ? (
                    <div className="bg-white/10 border border-white/20 p-4 rounded-2xl flex items-center gap-3">
                      <SafeIcon icon={FiLoader} className="text-indigo-300 animate-spin" />
                      <span className="text-xs font-black uppercase tracking-widest">Anke er under behandling</span>
                    </div>
                  ) : (
                    <button 
                      onClick={() => setShowAppealModal(true)}
                      className="bg-white text-indigo-900 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-50 transition-all shadow-lg flex items-center gap-2"
                    >
                      <SafeIcon icon={FiCornerUpLeft} /> Jeg ønsker å anke konklusjonen
                    </button>
                  )}
                </div>
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-300 mb-3">Oppsummering av utfall</h3>
                  <p className="text-sm font-medium italic">"{fullReport.outcome_summary || 'Ingen offentlig oppsummering tilgjengelig.'}"</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Evidence Requests Section */}
        {evidenceRequests.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Ubesvarte forespørsler om bevis</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {evidenceRequests.map(req => (
                <motion.div 
                  key={req.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-blue-600 text-white p-6 rounded-2xl shadow-xl flex justify-between items-center group overflow-hidden relative"
                >
                  <div className="absolute -right-4 -top-4 w-16 h-16 bg-white/10 rounded-full blur-xl group-hover:scale-150 transition-transform"></div>
                  <div>
                    <h3 className="font-black text-xs uppercase tracking-widest mb-1">{req.title}</h3>
                    <p className="text-xs text-blue-100 font-medium line-clamp-1">{req.description}</p>
                  </div>
                  <button 
                    onClick={() => setActiveRequest(req)}
                    className="bg-white text-blue-600 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-50 transition-all shadow-lg"
                  >
                    Last opp
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Deadline Indicator */}
        {deadlineDate && !isResolved && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            className={`rounded-2xl p-6 border flex items-center justify-between shadow-sm ${
              isOverdue ? 'bg-red-50 border-red-100 text-red-900' : 'bg-amber-50 border-amber-100 text-amber-900'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${isOverdue ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                <SafeIcon icon={isOverdue ? FiAlertCircle : FiClock} className="text-2xl" />
              </div>
              <div>
                <h3 className="font-black text-xs uppercase tracking-[0.2em]">Svarfrist for tilsvar</h3>
                <p className="text-lg font-bold">
                  {isOverdue ? 'Fristen er utgått' : `Du har ${daysLeft} ${daysLeft === 1 ? 'dag' : 'dager'} igjen til fristen`}
                </p>
                <p className="text-xs opacity-75 font-bold uppercase tracking-wider mt-1">
                  Dato: {format(new Date(deadlineDate), 'dd. MMMM yyyy', { locale: nb })}
                </p>
              </div>
            </div>
            <div className="hidden md:block">
              <SafeIcon icon={FiCalendar} className="text-4xl opacity-10" />
            </div>
          </motion.div>
        )}

        {/* Legal Info Box (System Generated - Locked) */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-blue-50 border border-blue-200 rounded-xl p-6" >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-blue-900 font-bold mb-2 flex items-center gap-2"> <SafeIcon icon={FiShield} /> Dine rettigheter og informasjon </h3>
              <p className="text-sm text-blue-800"> Du er part i denne saken og har rett til kontradiksjon. Det er viktig at du kjenner til prosessen. </p>
            </div>
            <button onClick={() => setShowRightsModal(true)} className="whitespace-nowrap bg-white text-blue-600 border border-blue-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm flex items-center gap-2" >
              <SafeIcon icon={FiBookOpen} /> Les om dine rettigheter
            </button>
          </div>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8">
          {/* Case Information (Restricted View) */}
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"> <SafeIcon icon={FiFileText} /> Sakens innhold </h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Sak ID</label>
                  <p className="font-mono text-gray-900">{reportId}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Beskrivelse av forholdet</label>
                  <div className="mt-2 bg-gray-50 p-4 rounded-lg border border-gray-100 text-gray-800 leading-relaxed whitespace-pre-wrap"> {sanitizedDescription} </div>
                  <p className="text-xs text-gray-400 mt-2 italic flex items-center gap-1"> <SafeIcon icon={FiInfo} /> Teksten kan være redigert for å skjerme tredjeparter. </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Trenger du bistand?</h3>
              <p className="text-sm text-gray-600 mb-4"> Du har rett til å la deg bistå av en tillitsvalgt, advokat eller annen rådgiver (f.eks. verneombud) i alle møter med arbeidsgiver som omhandler saken. </p>
            </div>
          </div>
          {/* Response Form */}
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"> <SafeIcon icon={FiMessageCircle} /> Ditt tilsvar </h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2"> Din redegjørelse * </label>
                  <p className="text-xs text-gray-500 mb-2"> Beskriv din versjon av hendelsene. Korriger eventuelle feil i faktum. </p>
                  <textarea 
                    value={responseContent} 
                    onChange={(e) => setResponseContent(e.target.value)} 
                    rows={12} 
                    disabled={isResolved}
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm leading-relaxed disabled:opacity-50" 
                    placeholder={isResolved ? "Saken er konkludert. Bruk ankeinstansen for videre innspill." : "Skriv ditt tilsvar her..."} 
                    required 
                  />
                </div>
                {!isResolved && (
                  <>
                    <div className="mb-6 bg-purple-50 p-4 rounded-lg border border-purple-100">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={wantsDialogue} onChange={(e) => setWantsDialogue(e.target.checked)} className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300" />
                        <span className="text-sm text-purple-900 font-medium"> Jeg ønsker et møte for videre dialog om saken </span>
                      </label>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <p className="text-xs text-gray-500"> Ditt svar lagres sikkert og logges med dato. </p>
                      <button type="submit" disabled={isSubmitting || !responseContent.trim()} className={`flex items-center gap-2 px-6 py-3 rounded-lg text-white font-medium transition-all ${isSubmitting || !responseContent.trim() ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 shadow-md'}`} >
                        {isSubmitting ? ( <> <SafeIcon icon={FiLoader} className="animate-spin" /> Sender... </> ) : ( <> <SafeIcon icon={FiSend} /> Send inn tilsvar </> )}
                      </button>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportedDashboard;